import { For, Show, createMemo, createSignal, onCleanup, onMount, type Component } from 'solid-js';
import { Portal } from 'solid-js/web';
import { Text } from '~ui/text';
import { formatCurrency, formatPercent } from '~/lib/format';

// A treemap, on trial.
//
// Not the house form for part-to-whole — that is a stacked bar, and it stays the
// default everywhere else. What a treemap buys over one is that eleven sectors
// of wildly different size stay separately readable: on a single stacked bar the
// tail becomes a row of slivers you cannot label or hit with a pointer, while
// here every one keeps a rectangle roughly proportional in both directions.
//
// What it costs is that area is harder to compare than length. Two tiles within
// a few percent of each other are indistinguishable by eye, which is precisely
// why this ships beside the table rather than instead of it — the table is the
// exact reading, and the map is the shape.
//
// No fill colour at all: an outlined box on the page's own surface. The tiles
// are nominal categories and area already encodes the value, so colouring them
// by size would double-encode it, and colouring them by identity does not
// survive the data — SMAX alone publishes eleven sectors against a palette of
// eight that is never cycled. An outline says "rectangle of this size" and
// nothing it has no business saying.
//
// Drawn on the theme's own two tones, so it inverts with the theme rather than
// being a light-mode picture shown on a dark page. Tiles sit flush — see the
// note on which edges each one draws.

interface Item {
    key: string;
    label: string;
    value: number;
    /** Share of the whole, already worked out by the caller. */
    share: number | null;
    /** Fuller name, for a label that had to be short enough to fit a rectangle.
        Shown on hover, never on the tile. */
    hint?: string;
    /**
     * Drawn as a muted block rather than an outlined one — a cash line, a
     * written-option line, anything that is not a position.
     *
     * Caller's call, because "has no ticker" means different things per axis: on
     * a company breakdown it is the cash line, on a sector breakdown it is every
     * row. Getting this from the data would mute an entire chart.
     */
    muted?: boolean;
    /**
     * How the tile divides between the funds that reach it, biggest first.
     *
     * Two funds holding XOM is one tile, because you own XOM once — but *how*
     * you own it is two positions, and a 75/25 split and a 50/50 one are
     * different facts about the same square. Given as weights in any units; the
     * tile normalises them.
     *
     * Drawn as rules inside the tile, in the same border colour as its edges. It
     * changes nothing else: not the geometry, not where the label sits, not what
     * the tooltip says. A tile with one source, or none given, draws none.
     */
    slices?: number[];
}

interface Tile extends Item {
    x: number;
    y: number;
    w: number;
    h: number;
}

/**
 * Where the rules go inside a tile, as percentages across it.
 *
 * Cumulative shares of everything but the last source — the final boundary is
 * the tile's own edge, which is already drawn. Returns nothing for a tile with
 * one source or none, which is most of them.
 */
const divisions = (t: Tile): number[] => {
    const parts = t.slices ?? [];
    if (parts.length < 2) return [];
    const total = parts.reduce((sum, v) => sum + v, 0);
    if (total <= 0) return [];

    const out: number[] = [];
    let run = 0;
    for (const v of parts.slice(0, -1)) {
        run += v;
        out.push((run / total) * 100);
    }
    return out;
};

/** Worst aspect ratio in a row laid along `length`, the quantity squarified
    treemaps minimise. Infinity for an empty row so the first item always joins. */
const worst = (row: number[], length: number, scale: number): number => {
    const areas = row.map((v) => v * scale);
    const sum = areas.reduce((a, b) => a + b, 0);
    if (sum <= 0 || length <= 0) return Infinity;
    const thickness = sum / length;
    let ratio = 0;
    for (const area of areas) {
        const side = area / thickness;
        if (side <= 0) return Infinity;
        ratio = Math.max(ratio, Math.max(thickness / side, side / thickness));
    }
    return ratio;
};

/**
 * Squarified treemap (Bruls, Huizing and van Wijk).
 *
 * Rows are laid along whichever side of the remaining rectangle is shorter, and
 * an item joins the current row only while doing so does not make the worst
 * rectangle in it less square. The alternative — slice-and-dice — is two lines
 * of code and produces slivers for anything past the first few items, which is
 * the failure this whole form exists to avoid.
 *
 * Everything is in percentages of the container, so the caller needs no measured
 * width and the layout survives a resize without recomputing.
 */
const squarify = (items: Item[]): Tile[] => {
    // Magnitude, not value. A levered fund carries its borrowing as a negative
    // cash line — HYLD's is −20.9% — and there is no such thing as negative
    // area, but dropping it drew the fund as though it were not levered at all.
    // Sized by how big it is and marked as not-a-position by its fill.
    const list = items.filter((i) => Math.abs(i.value) > 0).sort((a, b) => Math.abs(b.value) - Math.abs(a.value));
    const out: Tile[] = [];

    let x = 0;
    let y = 0;
    let width = 100;
    let height = 100;
    let remaining = list.reduce((sum, i) => sum + Math.abs(i.value), 0);
    let index = 0;

    while (index < list.length && width > 0.01 && height > 0.01 && remaining > 0) {
        const scale = (width * height) / remaining;
        // Down the left edge when the space is wider than tall, across the top
        // when it is taller than wide.
        const asColumn = width >= height;
        const length = asColumn ? height : width;

        const row = [Math.abs(list[index].value)];
        let next = index + 1;
        while (next < list.length && worst([...row, Math.abs(list[next].value)], length, scale) <= worst(row, length, scale)) {
            row.push(Math.abs(list[next].value));
            next += 1;
        }

        const rowSum = row.reduce((a, b) => a + b, 0);
        const thickness = (rowSum * scale) / length;
        let offset = 0;

        for (let i = 0; i < row.length; i += 1) {
            const size = (row[i] * scale) / thickness;
            const item = list[index + i];
            out.push(
                asColumn
                    ? { ...item, x, y: y + offset, w: thickness, h: size }
                    : { ...item, x: x + offset, y, w: size, h: thickness },
            );
            offset += size;
        }

        if (asColumn) {
            x += thickness;
            width -= thickness;
        } else {
            y += thickness;
            height -= thickness;
        }
        remaining -= rowSum;
        index += row.length;
    }

    return out;
};

export const Treemap: Component<{
    items: Item[];
    empty?: string;
    /** How to render a tile's value. Defaults to money, which is what Exposure
        measures; a fund's own breakdown is in percent and passes its own. */
    formatValue?: (value: number) => string;
    /** Makes tiles clickable. Absent leaves them inert rather than offering an
        affordance nothing is listening to. */
    onSelect?: (key: string) => void;
}> = (props) => {
    const tiles = createMemo(() => squarify(props.items));
    const [hovered, setHovered] = createSignal<string | null>(null);
    const [cursor, setCursor] = createSignal({ x: 0, y: 0 });
    const active = () => tiles().find((t) => t.key === hovered()) ?? null;
    const show = (value: number): string => (props.formatValue ?? formatCurrency)(value);

    // Share and value are two different facts on Exposure — what part of the
    // book, and how many dollars — but one fact on a fund's own breakdown, where
    // the value *is* the weight. Saying "25.0% · 25.0%" would be noise, so the
    // second is dropped when it renders identically.
    const detail = (t: Tile): string => {
        const value = show(t.value);
        const share = t.share == null ? null : formatPercent(t.share, 1);
        return share == null || share === value ? value : `${share} · ${value}`;
    };

    /**
     * Whether a tile can carry its text, measured in characters rather than in
     * percent of the plot.
     *
     * It used to be `w >= 16 && h >= 14` — a flat share of the container, blind
     * to what was going in it. That held a three-character ticker to the same bar
     * as "Toronto-Dominion Bank" and left a great many tiles blank that had room
     * for their label twice over. It was also unstable in the other direction: a
     * fixed percentage of a wide panel is a lot of pixels and of a narrow one is
     * hardly any, so the same tile labelled or not depending on the window.
     *
     * So the plot is measured and the tile's own pixels compared against the
     * string that would go in it. `CHAR` is a deliberate over-estimate of the
     * average advance at text-sm — labels here are mostly capitals and digits,
     * which are wider than lowercase — so the check errs toward not clipping.
     */
    const [box, setBox] = createSignal({ w: 0, h: 0 });
    let plot: HTMLDivElement | undefined;

    onMount(() => {
        if (plot == null) return;
        // Fires once on observe, so the first paint is unlabelled for a frame
        // and correct from the next one.
        const ro = new ResizeObserver(([entry]) => setBox({ w: entry.contentRect.width, h: entry.contentRect.height }));
        ro.observe(plot);
        onCleanup(() => ro.disconnect());
    });

    /** Average advance at text-sm, over-estimated. */
    const CHAR = 7.2;
    /** px-1 either side. */
    const PAD = 8;
    /** One line of leading-tight text-sm. */
    const LINE = 17;

    const holds = (t: Tile, text: string, lines: number): boolean => {
        const { w, h } = box();
        // Before the first measurement, fall back to the old share-of-plot rule
        // rather than blanking every tile for a frame.
        if (w <= 0 || h <= 0) return t.w >= 16 && t.h >= 14;
        return (t.w / 100) * w >= text.length * CHAR + PAD && (t.h / 100) * h >= lines * LINE;
    };

    /** The label alone needs one line. */
    const fitsLabel = (t: Tile): boolean => holds(t, t.label, 1);
    /** The figure under it needs a second, and is the wider of the two strings
        more often than not — so it is checked on its own rather than assumed to
        follow the label in. */
    const fitsDetail = (t: Tile): boolean => holds(t, detail(t), 2);

    return (
        <div class='flex min-h-0 min-w-0 flex-1 flex-col'>
            <Show when={tiles().length > 0} fallback={<div class='flex flex-1 items-center justify-center'><Text tone='muted'>{props.empty ?? 'Nothing to draw'}</Text></div>}>
                {/* Flush, and every shared edge drawn exactly once.
                    
                    Each tile draws only its top and left. An internal edge is
                    always some tile's top or left — the one below or to the right
                    of it — so it gets drawn by that neighbour and by nobody else.
                    Giving every tile all four sides is the obvious version and is
                    wrong: both sides of every shared line draw it and it renders
                    2px, twice the weight of the outer edge.
                    
                    That leaves the container's own right and bottom with nobody
                    to draw them, since no tile lies beyond to claim them; the
                    wrapper supplies those two. The outer top and left need
                    nothing — the tiles at x=0 and y=0 already draw them. */}
                <div ref={plot} class='relative min-h-0 flex-1 border-r border-b' style={{ 'border-color': 'var(--color-foreground)' }}>
                    <For each={tiles()}>
                        {(t) => (
                            <div
                                class='absolute flex flex-col items-center justify-center overflow-hidden border-t border-l px-1 text-center'
                                style={{
                                    left: `${t.x}%`,
                                    top: `${t.y}%`,
                                    width: `${t.w}%`,
                                    height: `${t.h}%`,
                                    'border-color': 'var(--color-foreground)',
                                    // A muted tile is filled rather than
                                    // outlined, so "this is not a position" is
                                    // legible before reading a word of it. It
                                    // still needs its own hover state, which the
                                    // plain muted fill would otherwise swallow.
                                    'background-color': t.muted
                                        ? hovered() === t.key
                                            ? 'color-mix(in srgb, var(--color-foreground) 22%, transparent)'
                                            : 'var(--color-muted)'
                                        : hovered() === t.key
                                          ? 'var(--color-muted)'
                                          : 'var(--color-background)',
                                }}
                                onMouseEnter={() => setHovered(t.key)}
                                onMouseMove={(e) => setCursor({ x: e.clientX, y: e.clientY })}
                                onMouseLeave={() => setHovered(null)}
                                onClick={props.onSelect ? () => props.onSelect!(t.key) : undefined}>
                                {/* The fund split, as rules across the tile.
                                    Absolute and pointer-events-none so they sit
                                    over the fill without touching the layout the
                                    label is centred in — the text is positioned
                                    by the flex box and does not know these are
                                    here.

                                    Cumulative offsets, one rule per boundary:
                                    two sources make one rule, three make two.
                                    The last boundary is the tile's own right
                                    edge and is already drawn. */}
                                <For each={divisions(t)}>
                                    {(left) => (
                                        // The ordinary border colour, not the
                                        // foreground the tile's own edges use. A
                                        // division inside a tile is a lesser rule
                                        // than the tile itself; drawn in the same
                                        // near-black it would compete with the
                                        // edges and read as two tiles rather than
                                        // one tile split.
                                        <span class='pointer-events-none absolute top-0 bottom-0 w-px' style={{ left: `${left.toFixed(3)}%`, 'background-color': 'var(--color-border)' }} />
                                    )}
                                </For>

                                {/* Two independent tests, not one. A tile wide
                                    enough for "RY" but only one line tall gets
                                    the ticker and no figure, where before it got
                                    neither — which is most of the small tiles on
                                    a company map. */}
                                <Show when={fitsLabel(t)}>
                                    <span class='w-full truncate text-sm leading-tight font-medium'>{t.label}</span>
                                </Show>
                                <Show when={fitsLabel(t) && fitsDetail(t)}>
                                    <span class='w-full truncate text-sm leading-tight'>
                                        <Text tone='muted'>{formatPercent(t.share, 1)}</Text>
                                    </span>
                                </Show>
                            </div>
                        )}
                    </For>
                </div>

                {/* Tooltip, not a footer row and not the browser's own.

                    A footer put the answer at the bottom of the panel while the
                    eye was on a tile in the middle, and `title` waits about a
                    second before showing anything — long enough that pointing at
                    a small tile feels like nothing happened. This appears the
                    moment the pointer lands.

                    Portalled and positioned from the pointer's viewport
                    coordinates: the tiles clip their own overflow and Panel is
                    overflow-hidden, so a tooltip drawn inside either would be cut
                    off against exactly the tiles it is describing. Flipped to the
                    other side of the cursor near an edge so it never leaves the
                    window. */}
                <Show when={active()}>
                    <Portal>
                        <div
                            class='pointer-events-none fixed z-[200] text-foreground max-w-72 rounded border bg-background px-2 py-1 shadow'
                            style={{
                                left: `${Math.min(cursor().x + 12, window.innerWidth - 300)}px`,
                                top: `${Math.min(cursor().y + 12, window.innerHeight - 72)}px`,
                            }}>
                            <div class='flex items-baseline gap-2 whitespace-nowrap'>
                                <Text weight='medium'>{active()!.label}</Text>
                                <Text tone='muted'>{detail(active()!)}</Text>
                            </div>
                            <Show when={active()!.hint}>
                                <Text tone='muted' class='block truncate'>
                                    {active()!.hint}
                                </Text>
                            </Show>
                        </div>
                    </Portal>
                </Show>
            </Show>
        </div>
    );
};

export default Treemap;
